import { useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, Landmark, type LucideIcon } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { getNavMeta } from "@/core/navigation/navMeta";
import { INICIO_ENTRY, CALENDARIO_ENTRY } from "@/core/navigation/modulesRegistry";

interface TabDef {
  label: string;
  icon: LucideIcon;
  path: string;
}

const TABS_BASE: TabDef[] = [
  { label: INICIO_ENTRY.label, icon: INICIO_ENTRY.icon, path: "/inicio" },
  { label: CALENDARIO_ENTRY.label, icon: CALENDARIO_ENTRY.icon, path: "/calendario" },
  { label: "Central Elos", icon: LayoutGrid, path: "/modulos" },
];

const TAB_NUCLEO: TabDef = { label: "Administração", icon: Landmark, path: "/nucleo" };

/**
 * Sobe a cadeia de `parent` do navMeta até a raiz. Usado para saber qual aba
 * deve acender mesmo estando numa subtela — ex.: dentro de "Reservas" (que
 * aponta parent: "/modulos"), a aba "Central Elos" continua marcada como ativa.
 */
function rotaRaiz(pathname: string): string {
  let atual = pathname;
  for (let i = 0; i < 10; i += 1) {
    const meta = getNavMeta(atual);
    if (meta.parent === null) return atual;
    atual = meta.parent;
  }
  return atual;
}

/**
 * Barra fixa inferior (Sprint 3 — redesign Elos, protótipo
 * `proto-navegacao-elos.html` aprovado). Início/Calendário/Módulos sempre
 * aparecem; Núcleo só para quem é Admin da Plataforma (as outras 3 abas não
 * têm nada de admin-only, então continuam abertas a todo autenticado).
 *
 * Substitui a navegação entre Início/Calendário/Módulos que antes vivia
 * dentro do menu hambúrguer (ModuleDrawer) — agora fica sempre visível, um
 * toque de distância. O hambúrguer ficou só para "Conta" (perfil/sair/tema).
 */
export function BottomTabBar() {
  const { ehAdminPlataforma } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = ehAdminPlataforma ? [...TABS_BASE, TAB_NUCLEO] : TABS_BASE;
  const raiz = rotaRaiz(location.pathname);

  return (
    <nav
      aria-label="Navegação principal"
      className="fixed inset-x-0 bottom-0 z-20 flex border-t border-border bg-surface/95 backdrop-blur"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {tabs.map((tab) => {
        const ativo = raiz === tab.path;
        const Icone = tab.icon;
        return (
          <button
            key={tab.path}
            type="button"
            onClick={() => navigate(tab.path)}
            aria-current={ativo ? "page" : undefined}
            className={`flex min-w-0 flex-1 flex-col items-center gap-1 py-2.5 font-semibold transition-colors ${
              ativo ? "text-primary" : "text-muted hover:text-text"
            }`}
          >
            <Icone size={22} />
            {/* Ajuste B (N-18): duas linhas reservadas em todas as abas para os ícones
                ficarem alinhados; quebra só nos espaços, equilibrada; fonte fluida 9,5–11px. */}
            <span
              className="flex min-h-[2.3em] items-start justify-center text-center leading-[1.15]"
              style={{
                fontSize: "clamp(9.5px, 2.9vw, 11px)",
                textWrap: "balance",
                overflowWrap: "normal",
                hyphens: "none",
              }}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
